// src/pages/Analytics.js — Workspace product metrics + user-defined formulas
import { useState, useEffect, useMemo, useCallback } from "react";
import { doc, onSnapshot, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../lib/firebase";
import {
  watchContacts,
  watchPaymentsCollection,
  watchOrdersCollection,
  watchScansCollection,
  watchFilesCollection,
  watchWorkspacesCollection,
  watchWorkspaceMembersCollection,
  watchProductAnalyticsEventsCollection,
} from "../lib/db";
import { buildMetricVariables, monthlyRevenueSeries } from "../lib/metrics";
import { evaluateFormula } from "../lib/formulaEval";
import AnalyticsDrillModal from "../components/AnalyticsDrillModal";
import { computeFunnelAnalytics, FILES_PER_COMPLETE_SCAN, downloadCohortCsv } from "../lib/productAnalyticsMetrics";
import { computeWorkspaceGeoConcentration } from "../lib/workspaceGeoAnalytics";
import { formatSqft } from "../lib/scanSqft";
import GrowthAnalyticsSection from "../components/GrowthAnalyticsSection";
import DeviceAnalyticsSection from "../components/DeviceAnalyticsSection";
import ClickAnalyticsSection from "../components/ClickAnalyticsSection";
import DataQualityBar, { loadDataQuality } from "../components/DataQualityBar";
import { computeGrowthAnalytics } from "../lib/growthAnalyticsMetrics";
import { buildRevenueChartSeries } from "../lib/revenueChartSeries";
import MetricGrid from "../components/MetricGrid";
import AreaChart from "../components/charts/AreaChart";
import PeriodToggle, { PERIOD_LABELS } from "../components/charts/PeriodToggle";
import KpiCard from "../components/charts/KpiCard";
import CohortCard from "../components/CohortCard";
import FunnelChart from "../components/charts/FunnelChart";
import Heatmap from "../components/charts/Heatmap";
import AlertBanner from "../components/AlertBanner";
import {
  Activity,
  Clock,
  Scan,
  ShoppingCart,
  Repeat,
  Timer,
  Users,
  DollarSign,
  Globe,
  Target,
  UserX,
  Ruler,
  Download,
} from "lucide-react";

const ANALYTICS_REF = doc(db, "crm_settings", "analytics");

function sparklineValues(expression, baseVars, monthTotals) {
  return monthTotals.map((rev) => {
    try {
      return evaluateFormula(expression, { ...baseVars, revenue: rev, r: rev });
    } catch {
      return 0;
    }
  });
}

function MiniBars({ values, height = 44 }) {
  const max = Math.max(1, ...values.map((v) => Math.abs(v)));
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height, marginTop: 10 }}>
      {values.map((v, i) => {
        const h = Math.round((Math.abs(v) / max) * (height - 4));
        return (
          <div
            key={i}
            style={{
              flex: 1,
              height: Math.max(2, h),
              borderRadius: 3,
              background: "rgba(14,165,233,0.45)",
            }}
            title={Number.isFinite(v) ? String(v) : ""}
          />
        );
      })}
    </div>
  );
}

export default function Analytics({ onOpenContact, onGoFinances, onGoEngagement }) {
  const [contacts, setContacts] = useState([]);
  const [payments, setPayments] = useState([]);
  const [orders, setOrders] = useState([]);
  const [scans, setScans] = useState([]);
  const [files, setFiles] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [workspaceMembers, setWorkspaceMembers] = useState([]);
  const [productAnalyticsEvents, setProductAnalyticsEvents] = useState([]);
  const [customRows, setCustomRows] = useState([]);
  const [loadErr, setLoadErr] = useState(null);
  const [saving, setSaving] = useState(false);

  const [draftName, setDraftName] = useState("");
  const [draftFormula, setDraftFormula] = useState("revenue_mtd / paid_customers");
  const [drill, setDrill] = useState(null);
  const [revenuePeriod, setRevenuePeriod] = useState("monthly");
  const [dataQuality, setDataQuality] = useState(() => loadDataQuality());

  useEffect(() => {
    const u1 = watchContacts((d) => setContacts(d), () => setLoadErr("Contacts failed"));
    const u2 = watchPaymentsCollection((d) => setPayments(d), () => {});
    const u3 = watchOrdersCollection((d) => setOrders(d), () => {});
    const u4 = watchScansCollection((d) => setScans(d), () => {});
    const u4b = watchFilesCollection((d) => setFiles(d), () => {});
    const u5 = watchWorkspacesCollection((d) => setWorkspaces(d), () => {});
    const u6 = watchWorkspaceMembersCollection((d) => setWorkspaceMembers(d), () => {});
    const u7 = watchProductAnalyticsEventsCollection((d) => setProductAnalyticsEvents(d), () => {});
    return () => {
      u1();
      u2();
      u3();
      u4();
      u4b();
      u5();
      u6();
      u7();
    };
  }, []);

  useEffect(() => {
    return onSnapshot(
      ANALYTICS_REF,
      (snap) => {
        const data = snap.data() || {};
        const list = Array.isArray(data.customMetrics) ? data.customMetrics : [];
        setCustomRows(list.filter((x) => x && x.id && x.name && x.formula));
      },
      () => {}
    );
  }, []);

  const vars = useMemo(
    () =>
      buildMetricVariables({
        contacts,
        payments,
        orders,
        scans,
        workspaces,
        workspaceMembers,
      }),
    [contacts, payments, orders, scans, workspaces, workspaceMembers]
  );

  const funnel = useMemo(
    () => computeFunnelAnalytics(contacts, scans, orders, payments, files, productAnalyticsEvents),
    [contacts, scans, orders, payments, files, productAnalyticsEvents]
  );

  const geoConcentration = useMemo(() => computeWorkspaceGeoConcentration(workspaces), [workspaces]);

  const growthMetrics = useMemo(
    () => computeGrowthAnalytics(contacts, files, { payments, orders }),
    [contacts, files, payments, orders]
  );

  const { labels: monthLabels, totals: monthTotals } = useMemo(
    () => monthlyRevenueSeries(payments, contacts, 12),
    [payments, contacts]
  );

  const revenueChart = useMemo(
    () => buildRevenueChartSeries(payments, contacts, revenuePeriod, dataQuality),
    [payments, contacts, revenuePeriod, dataQuality]
  );

  const persistCustom = useCallback(
    async (nextList) => {
      setSaving(true);
      try {
        await setDoc(
          ANALYTICS_REF,
          { customMetrics: nextList, updatedAt: serverTimestamp() },
          { merge: true }
        );
      } finally {
        setSaving(false);
      }
    },
    []
  );

  function addCustom(e) {
    e.preventDefault();
    const name = draftName.trim();
    const formula = draftFormula.trim();
    if (!name || !formula) return;
    const id = `cm_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
    const next = [...customRows, { id, name, formula }];
    setDraftName("");
    persistCustom(next);
  }

  function removeCustom(id) {
    persistCustom(customRows.filter((r) => r.id !== id));
  }

  return (
    <div className="page-wrap" style={{ maxWidth: 1100 }}>
      {loadErr && <AlertBanner variant="error">{loadErr}</AlertBanner>}

      <header className="page-header">
        <h1 className="page-header-title">Product analytics</h1>
        <p className="page-header-desc">
          Live metrics from Firestore collections your app already writes (<code>payments</code>, <code>orders</code>,{" "}
          <code>scans</code>, <code>workspaces</code>, <code>users</code>). Revenue excludes contacts marked{" "}
          <strong>internal</strong>.
        </p>
      </header>

      <DataQualityBar onChange={setDataQuality} />

      <GrowthAnalyticsSection
        contacts={contacts}
        files={files}
        payments={payments}
        orders={orders}
        scans={scans}
        workspaces={workspaces}
        workspaceMembers={workspaceMembers}
        productAnalyticsEvents={productAnalyticsEvents}
        onDrill={setDrill}
      />

      <DeviceAnalyticsSection contacts={contacts} onDrill={setDrill} />

      <ClickAnalyticsSection productAnalyticsEvents={productAnalyticsEvents} />

      <div className="analytics-section card">
        <div className="chart-card-header">
          <div>
            <h2 className="text-h3">Acquisition funnel</h2>
            <p className="page-header-desc" style={{ marginBottom: 0 }}>
              Scanbrix journey from file activity through paid conversion (existing CRM + product metrics).
            </p>
          </div>
        </div>
        <FunnelChart
          stages={[
            { label: "CRM profiles", value: vars.contacts_total ?? 0, color: "var(--secondary-500)" },
            { label: "Downloads", value: growthMetrics.totalDownloads ?? 0, color: "var(--purple)" },
            { label: "Signups", value: funnel.signupsTotal ?? 0, color: "var(--accent)" },
            { label: "Activated", value: funnel.activatedContactCount ?? 0, color: "var(--info-500)" },
            {
              label: "Paid",
              value: funnel.payingCustomersCount ?? 0,
              color: "var(--success-500)",
            },
          ]}
        />
      </div>

      <div className="analytics-section">
        <h2 className="text-h3" style={{ marginBottom: 8 }}>Activation & monetization funnel</h2>
        <p className="page-header-desc" style={{ marginBottom: 16 }}>
          Calculated from CRM sign-ups (<code>{process.env.REACT_APP_CRM_CONTACTS_COLLECTION || "users"}</code>, non-internal)
          matched to <code>scans</code> and <code>files</code> by user id (<code>created_by</code>, <code>uploadedBy</code>, etc.) or email.
          Completion = processed scan (<code>area_sq_ft</code>), same session/workspace with{" "}
          <strong>{FILES_PER_COMPLETE_SCAN}</strong> uploads, explicit “complete” flags, or{" "}
          <strong>≥{FILES_PER_COMPLETE_SCAN}</strong> upload docs per user across separate Firestore docs.
        </p>
      </div>

      <MetricGrid
        className="analytics-section"
        onDrill={setDrill}
        items={[
          {
            key: "activation",
            icon: Activity,
            label: "Activation rate",
            value:
              funnel.activationRatePct == null
                ? "—"
                : `${funnel.activationRatePct.toFixed(1)}%`,
            sub: `${funnel.activatedContactCount}/${funnel.signupsTotal || 0} sign-ups scanned · click details`,
            drill: {
              type: "funnel_drill",
              funnelKey: "activation_rate",
              title: "Activation rate — sign-ups vs first completed scan",
            },
          },
          {
            key: "ttfs",
            icon: Clock,
            label: "Time to first scan (median)",
            value:
              funnel.medianSignupToScanDays == null
                ? "—"
                : `${funnel.medianSignupToScanDays.toFixed(1)} d`,
            sub: "among users who complete a scan",
            drill: {
              type: "funnel_drill",
              funnelKey: "time_to_first_scan",
              title: "Time to first completed scan",
            },
          },
          {
            key: "scan_complete",
            icon: Scan,
            label: "Scan completion rate",
            value:
              funnel.scanCompletionRatePct == null
                ? "—"
                : `${funnel.scanCompletionRatePct.toFixed(1)}%`,
            sub: `${funnel.scanSessionsComplete}/${funnel.scanSessionsTotal || 0} sessions`,
            drill: {
              type: "funnel_drill",
              funnelKey: "scan_completion",
              title: "Scan sessions · complete vs abandoned",
            },
          },
          {
            key: "scan_order",
            icon: ShoppingCart,
            label: "Scan → paid order rate",
            value:
              funnel.scanToOrderRatePct == null
                ? "—"
                : `${funnel.scanToOrderRatePct.toFixed(1)}%`,
            sub: `${funnel.paidActivatedCount}/${funnel.activatedProductUsers || 0} scanned users`,
            drill: {
              type: "funnel_drill",
              funnelKey: "scan_to_order",
              title: "Activated scanners → paid order",
            },
          },
          {
            key: "ttfo",
            icon: Timer,
            label: "Time to first order (median)",
            value:
              funnel.medianScanToFirstPaidOrderDays == null
                ? "—"
                : `${funnel.medianScanToFirstPaidOrderDays.toFixed(1)} d`,
            sub: "first scan → first paid order (both exist)",
            drill: {
              type: "funnel_drill",
              funnelKey: "time_first_order",
              title: "Scan to first paid order",
            },
          },
          {
            key: "repeat90",
            icon: Repeat,
            label: "Repeat order ≤90 days",
            value:
              funnel.repeatOrderWithin90Pct == null
                ? "—"
                : `${funnel.repeatOrderWithin90Pct.toFixed(1)}%`,
            sub:
              funnel.repeatOrderBaseCustomers != null
                ? `${funnel.repeatWithin90Numerator}/${funnel.repeatOrderBaseCustomers} paying customers · 2nd order ≤90d after 1st`
                : "",
            drill: {
              type: "funnel_drill",
              funnelKey: "repeat_90",
              title: "Second order within 90 days",
            },
          },
          {
            key: "between_orders",
            icon: Timer,
            label: "Time between orders 1→2",
            value:
              funnel.medianDaysBetweenFirstTwoOrders == null
                ? "—"
                : `${funnel.medianDaysBetweenFirstTwoOrders.toFixed(1)} d`,
            sub: "median gap · repeat purchasers only",
            drill: {
              type: "funnel_drill",
              funnelKey: "time_between_orders",
              title: "Days between paid order #1 and #2",
            },
          },
          {
            key: "opc",
            icon: Users,
            label: "Orders per customer",
            value:
              funnel.ordersPerCustomerLifetime == null || Number.isNaN(funnel.ordersPerCustomerLifetime)
                ? "—"
                : funnel.ordersPerCustomerLifetime.toLocaleString(undefined, { maximumFractionDigits: 3 }),
            sub:
              funnel.totalPaidOrders != null && funnel.signupsTotal != null
                ? `${funnel.totalPaidOrders} orders ÷ ${funnel.signupsTotal} sign-ups`
                : "",
            drill: {
              type: "funnel_drill",
              funnelKey: "orders_per_customer",
              title: "Orders per CRM contact",
            },
          },
          {
            key: "ltv",
            icon: DollarSign,
            accent: "success",
            label: "Customer LTV (avg)",
            value:
              funnel.customerLtvAvgUsd == null
                ? "—"
                : `$${Math.round(funnel.customerLtvAvgUsd).toLocaleString()}`,
            sub:
              funnel.payingCustomersCount != null
                ? `$${Math.round(funnel.revenueUsdAttributed || 0).toLocaleString()} ÷ ${funnel.payingCustomersCount} payer(s)`
                : "successful payments ÷ payer count",
            drill: {
              type: "funnel_drill",
              funnelKey: "ltv",
              title: "Lifetime payments by customer",
            },
          },
        ]}
      />

      <div className="analytics-section">
        <h2 className="text-h3" style={{ marginBottom: 8 }}>Drop-off cohorts</h2>
        <p className="page-header-desc" style={{ marginBottom: 16 }}>
          Counts use <strong>email addresses</strong> from CRM sign-ups (non-internal). Click a card for the user list, or
          export CSV from the card.
        </p>
      </div>

      <div className="stats-grid analytics-section">
        {[
          {
            key: "abandoned_scans",
            icon: Scan,
            label: "Abandoned scans",
            value: String(funnel.abandonedScansCount ?? 0),
            sub: `processed scan or ≥${FILES_PER_COMPLETE_SCAN} uploads, no paid order`,
            drill: {
              type: "funnel_drill",
              funnelKey: "abandoned_scans",
              title: "Abandoned scans — uploaded but never ordered",
            },
          },
          {
            key: "abandoned_sqft",
            icon: Ruler,
            label: "Abandoned scan sq ft",
            value:
              (funnel.abandonedSqftTotal ?? 0) > 0
                ? formatSqft(funnel.abandonedSqftTotal, { fallback: "—" })
                : "—",
            sub:
              (funnel.abandonedSqftTotal ?? 0) > 0
                ? `${funnel.abandonedWithSqftCount ?? 0} user(s) with area · no paid order`
                : "sq ft on completed scans, no order yet",
            drill: {
              type: "funnel_drill",
              funnelKey: "abandoned_sqft",
              title: "Abandoned scan square footage",
            },
          },
          {
            key: "never_scanned",
            icon: UserX,
            label: "Signed up, never scanned",
            value: String(funnel.signedUpNeverScannedCount ?? 0),
            sub: "CRM profiles with email · zero scan docs",
            drill: {
              type: "funnel_drill",
              funnelKey: "signed_up_never_scanned",
              title: "Signed up, never scanned",
            },
          },
          {
            key: "no_upload",
            icon: Download,
            label: "Scanned once, never came back",
            value: String(funnel.scannedOnceNoUploadCount ?? 0),
            sub: "scan activity · zero uploaded files",
            drill: {
              type: "funnel_drill",
              funnelKey: "scanned_no_upload",
              title: "Scanned but no file uploads",
            },
          },
        ].map((k) => (
          <CohortCard
            key={k.key}
            label={k.label}
            value={k.value}
            sub={k.sub}
            icon={k.icon}
            onDrill={() => setDrill(k.drill)}
            onExport={() =>
              downloadCohortCsv(
                k.drill.funnelKey,
                contacts,
                scans,
                orders,
                payments,
                files,
                productAnalyticsEvents
              )
            }
          />
        ))}
      </div>

      <div className="analytics-section">
        <h2 className="text-h3" style={{ marginBottom: 8 }}>Geography</h2>
        <p className="page-header-desc" style={{ marginBottom: 16 }}>
          Based on workspace addresses at creation. Country is inferred from city and address text when the stored
          country is incorrectly set to USA.
        </p>
      </div>

      <div className="two-col analytics-section">
        <KpiCard
          label="Geographical concentration"
          icon={Globe}
          value={geoConcentration.levels.country.length || "—"}
          comparison={
            geoConcentration.totalWithAddress > 0
              ? `${geoConcentration.totalWithAddress} located workspace${geoConcentration.totalWithAddress === 1 ? "" : "s"} · click for drill-down`
              : "no addresses yet"
          }
          onClick={() =>
            setDrill({
              type: "geo_concentration",
              level: "country",
              title: "Geographical concentration",
            })
          }
          as="button"
        />
        <div className="card card-sm">
          <div className="section-title" style={{ marginBottom: 12 }}>
            Top countries
          </div>
          <div className="geo-list">
            {geoConcentration.levels.country.slice(0, 5).map((row) => {
              const max = Math.max(1, geoConcentration.levels.country[0]?.count || 1);
              return (
                <div key={row.label} className="geo-row">
                  <div>
                    <div className="geo-row-label">{row.label}</div>
                    <div className="geo-row-bar">
                      <div className="geo-row-fill" style={{ width: `${Math.round((row.count / max) * 100)}%` }} />
                    </div>
                  </div>
                  <span className="text-caption">{row.count}</span>
                </div>
              );
            })}
            {geoConcentration.levels.country.length === 0 && (
              <div className="text-caption">No workspace addresses yet.</div>
            )}
          </div>
        </div>
        <div className="card card-sm">
          <div className="section-title" style={{ marginBottom: 12 }}>
            Regional heatmap
          </div>
          <Heatmap
            cells={geoConcentration.levels.country.slice(0, 12).map((row) => ({
              label: row.label,
              value: row.count,
            }))}
            emptyLabel="No regional data yet."
          />
        </div>
      </div>

      <h2 className="text-h3 analytics-section" style={{ marginBottom: 12 }}>
        Quick counts
      </h2>

      <MetricGrid
        onDrill={setDrill}
        items={[
          {
            key: "rev_mtd",
            icon: DollarSign,
            accent: "success",
            label: "Invoice revenue (MTD)",
            value: `$${Math.round(vars.revenue_mtd || 0).toLocaleString()}`,
            sub: "payments · this month",
            sparkline: monthTotals,
            drill: { type: "payments_mtd", title: "Payments · this month" },
          },
          {
            key: "paid",
            icon: Users,
            label: "Paid customers",
            value: String(vars.paid_customers ?? 0),
            sub: "click list · Engagement for retargeting",
            drill: { type: "paid_contacts", title: "Paid customers" },
            extra: onGoEngagement ? (
              <button
                type="button"
                className="btn btn-sm"
                style={{ marginTop: 8 }}
                onClick={(e) => {
                  e.stopPropagation();
                  onGoEngagement();
                }}
              >
                Open Engagement
              </button>
            ) : null,
          },
          {
            key: "orders",
            icon: ShoppingCart,
            label: "Orders (30d)",
            value: String(vars.orders_30d ?? 0),
            sub: "orders collection",
            drill: { type: "orders_30d", title: "Orders · last 30 days" },
          },
          {
            key: "scans",
            icon: Scan,
            label: "Scans (30d)",
            value: String(vars.scans_30d ?? 0),
            sub: "scans collection",
            drill: { type: "scans_30d", title: "Scans · last 30 days" },
          },
          {
            key: "ws",
            icon: Target,
            label: "Workspaces",
            value: String(vars.workspaces_total ?? 0),
            sub: "total rows",
            drill: { type: "workspaces", title: "Workspaces" },
          },
          {
            key: "wsm",
            icon: Users,
            label: "Workspace members",
            value: String(vars.workspace_members_total ?? 0),
            sub: "membership rows",
            drill: { type: "workspace_members", title: "Workspace members" },
          },
          {
            key: "contacts",
            icon: Users,
            label: "Contacts (non-internal)",
            value: String(vars.contacts_total ?? 0),
            sub: "CRM profiles",
            drill: { type: "contacts_noninternal", title: "Contacts (non-internal)" },
          },
          {
            key: "pay_count",
            icon: DollarSign,
            label: "Successful payments (MTD)",
            value: String(vars.payments_count_mtd ?? 0),
            sub: "same rows as invoice revenue",
            drill: { type: "payments_mtd", title: "Successful payments · MTD" },
          },
        ]}
      />

      <AnalyticsDrillModal
        open={Boolean(drill)}
        drill={drill}
        onClose={() => setDrill(null)}
        contacts={contacts}
        payments={payments}
        orders={orders}
        scans={scans}
        files={files}
        workspaces={workspaces}
        workspaceMembers={workspaceMembers}
        productAnalyticsEvents={productAnalyticsEvents}
        growth={growthMetrics}
        onOpenContact={onOpenContact}
        onGoFinances={onGoFinances}
      />

      <div className="card" style={{ marginBottom: 20 }}>
        <div className="chart-card-header">
          <div>
            <div className="section-title">Revenue · last 12 months</div>
            <div className="section-sub">
              {PERIOD_LABELS[revenuePeriod]} view · same calculation as Dashboard (successful payments, excl. internal)
            </div>
          </div>
          <PeriodToggle value={revenuePeriod} onChange={setRevenuePeriod} />
        </div>
        {revenueChart.values.every((v) => v === 0) ? (
          <div style={{ padding: 16, color: "var(--text-3)", fontSize: 13 }}>No revenue in range yet.</div>
        ) : (
          <AreaChart
            values={revenueChart.values}
            labels={revenueChart.labels}
            height={360}
            accent="var(--chart-line)"
          />
        )}
      </div>

      <div className="card">
        <div className="section-header">
          <div>
            <div className="section-title">Custom metrics</div>
            <div className="section-sub">
              Formulas use variables like <code>revenue_mtd</code>, <code>paid_customers</code>, <code>orders_30d</code>,{" "}
              <code>revenue_m0</code>…<code>revenue_m11</code> (12 months, oldest→newest). Operators: + − * / ( ). The small chart
              substitutes <code>revenue</code> per month for the bar series.
            </div>
          </div>
        </div>

        <form onSubmit={addCustom} style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 20, alignItems: "flex-end" }}>
          <label style={{ flex: "1 1 180px", fontSize: 12, color: "var(--text-2)" }}>
            Name
            <input className="input" value={draftName} onChange={(e) => setDraftName(e.target.value)} placeholder="e.g. Rough ARPU" style={{ marginTop: 4, width: "100%" }} />
          </label>
          <label style={{ flex: "2 1 280px", fontSize: 12, color: "var(--text-2)" }}>
            Formula
            <input
              className="input"
              value={draftFormula}
              onChange={(e) => setDraftFormula(e.target.value)}
              placeholder="revenue_mtd / paid_customers"
              style={{ marginTop: 4, width: "100%", fontFamily: "ui-monospace, monospace", fontSize: 12 }}
            />
          </label>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            Add metric
          </button>
        </form>

        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {customRows.length === 0 ? (
            <div style={{ color: "var(--text-3)", fontSize: 13 }}>No custom metrics yet. Add one above.</div>
          ) : (
            customRows.map((row) => {
              let scalar = null;
              let err = null;
              try {
                scalar = evaluateFormula(row.formula, vars);
              } catch (e) {
                err = e?.message || "Invalid formula";
              }
              const spark = sparklineValues(row.formula, vars, monthTotals);
              return (
                <div
                  key={row.id}
                  style={{
                    padding: 14,
                    borderRadius: 10,
                    border: "0.5px solid var(--border)",
                    background: "var(--bg-elevated)",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{row.name}</div>
                      <div style={{ fontSize: 11, color: "var(--text-3)", fontFamily: "ui-monospace, monospace", marginTop: 4 }}>
                        {row.formula}
                      </div>
                    </div>
                    <button type="button" className="btn btn-sm btn-ghost" onClick={() => removeCustom(row.id)}>
                      Remove
                    </button>
                  </div>
                  {err ? (
                    <div style={{ marginTop: 10, color: "var(--red)", fontSize: 13 }}>{err}</div>
                  ) : (
                    <>
                      <div style={{ fontSize: 28, fontWeight: 700, color: "var(--accent)", marginTop: 8 }}>
                        {Number.isFinite(scalar) ? scalar.toLocaleString(undefined, { maximumFractionDigits: 2 }) : "—"}
                      </div>
                      <MiniBars values={spark} />
                    </>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
